import React, { useContext, useEffect, useState } from 'react';
import { CreditCard, User, Check, ArrowLeft, ArrowRight, DollarSign, Calendar, Gift, Star, Shield, Zap, IndianRupee } from 'lucide-react';
import styles from './SellMembership.module.css';
import MembershipNavbar from '../../Components/Membership/MembershipNavbar';
import { BASE_URL_API } from '../../References/URLs';
import AuthContext from '../../store/auth-context';
import ActiveStoreContext from '../../store/ActiveStoreContext';
import AddNewClient from '../../Components/Users/AddNewClient';
import { ShimmerButton } from 'react-shimmer-effects';
import OtpInput from 'react-otp-input';
import { BiLoaderAlt } from 'react-icons/bi';
import { humanReadableTimePassed } from '../../Helper/calculations';
import Select from 'react-select'
import { Link, useNavigate } from 'react-router';
import { toast } from 'react-toastify';
// Import statements for lucide-react
import {
    UserCheck, Rocket, Compass, Package, Target, Plus, TrendingUp, Trophy, Medal, Settings, Crown, Users, Flag, Gem, Diamond, Lock, Briefcase, GraduationCap, Building, Building2, 
    Award, Signature, Sparkles, Sceptre, Circle, Hammer, Eye, Medal as ChampionMedal, Scroll, Clock, Heart, Globe, Handshake, ShieldCheck, Lightbulb, MapPin, Telescope, Infinity, ArrowUp
}
    from 'lucide-react';
// Alternative imports from react-icons (if preferred)
import {
    FaUser, FaUserCheck, FaRocket, FaCompass, FaBox, FaBullseye, FaPlus, FaChartLine, FaTrophy,
    FaMedal, FaBolt, FaCog, FaCrown, FaUsers, FaFlag, FaStar, FaGem, FaDiamond, FaLock, FaShield, FaBriefcase, FaGraduationCap,
    FaBuilding, FaIndustry, FaUserTie,
    FaChessKing, FaAward, FaSignature, FaMagic, FaChessQueen, FaCircle, FaHammer, FaEye, FaMedal as FaChampionMedal, FaScroll, FaClock, FaHeart, FaGlobe, FaHandshake, FaShieldAlt, FaLightbulb, FaMapMarkerAlt, FaTelescope, FaInfinity, FaArrowUp
} from 'react-icons/fa';

const SellMembership = () => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => { try { return asCtx?.activeStore && asCtx.activeStore.trim() !== "" ? JSON.parse(asCtx.activeStore) : null; } catch { return null; } })();
    const vendorIdentifier = JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier
    const [isCreating, setIsCreating] = useState(false)
    const [isFetching, setIsFetching] = useState(false)
    const [isUserFormOpen, setIsUserFormOpen] = useState(false)
    const [userDetailsFetched, setUserDetailsFetched] = useState(false)
    const [availableSeries, setAvailableSeries] = useState([])
    const [isFetchingSeries, setIsFetchingSeries] = useState(false)
    const [selectCardNumber, setSelectCardNumber] = useState(null)
    const navigate = useNavigate()

    const [isLoading, setIsLoading] = useState(false)
    const [phoneNumber, setPhoneNumber] = useState({
        otp: ''
    })
    const [userDetails, setUserDetails] = useState(null);

    const [currentStep, setCurrentStep] = useState(1)
    const [selectedCard, setSelectedCard] = useState(null)
    const [isNextBtnDisable, setIsNextBtnDisable] = useState(true)

    // Membership tier icon mappings
    const membershipIcons = {
        // Entry Level
        free: <User className={styles.cardIcon} />,
        basic: <UserCheck className={styles.cardIcon} />,
        starter: <Rocket className={styles.cardIcon} />,
        explorer: <Compass className={styles.cardIcon} />,
        essential: <Package className={styles.cardIcon} />,

        // Standard Level  
        standard: <Target className={styles.cardIcon} />,
        plus: <Plus className={styles.cardIcon} />,
        growth: <TrendingUp className={styles.cardIcon} />,
        achiever: <Trophy className={styles.cardIcon} />,
        silver: <Medal className={styles.cardIcon} />,

        // Professional Level
        pro: <Zap className={styles.cardIcon} />,
        advanced: <Settings className={styles.cardIcon} />,
        gold: <Crown className={styles.cardIcon} />,
        leader: <Users className={styles.cardIcon} />,
        priority: <Flag className={styles.cardIcon} />,

        // Premium Level
        premium: <Star className={styles.cardIcon} />,
        platinum: <Gem className={styles.cardIcon} />,
        diamond: <Diamond className={styles.cardIcon} />,
        exclusive: <Lock className={styles.cardIcon} />,
        vip: <Shield className={styles.cardIcon} />,

        // Elite Level
        business: <Briefcase className={styles.cardIcon} />,
        professional: <GraduationCap className={styles.cardIcon} />,
        enterprise: <Building className={styles.cardIcon} />,
        corporate: <Building2 className={styles.cardIcon} />,
        executive: <FaUserTie className={styles.cardIcon} />,

        // Ultra-Premium Level
        royal: <Crown className={styles.cardIcon} />,
        prestige: <Award className={styles.cardIcon} />,
        signature: <Signature className={styles.cardIcon} />,
        elite: <Sparkles className={styles.cardIcon} />,
        sovereign: <Crown className={styles.cardIcon} />,
        innerCircle: <Circle className={styles.cardIcon} />,
        founder: <Hammer className={styles.cardIcon} />,
        visionary: <Eye className={styles.cardIcon} />,
        champion: <Trophy className={styles.cardIcon} />,
        legend: <Scroll className={styles.cardIcon} />,
        lifetime: <Clock className={styles.cardIcon} />,
        honorary: <Heart className={styles.cardIcon} />,
        ambassador: <Globe className={styles.cardIcon} />,
        patron: <Handshake className={styles.cardIcon} />,
        guardian: <ShieldCheck className={styles.cardIcon} />,
        innovator: <Lightbulb className={styles.cardIcon} />,
        pioneer: <MapPin className={styles.cardIcon} />,
        futurist: <Telescope className={styles.cardIcon} />,
        infinity: <Infinity className={styles.cardIcon} />,
        beyond: <ArrowUp className={styles.cardIcon} />
    };

    const getClientDetails = async () => {
        setIsLoading(true)
        const getClientDetailsResponse = await fetch(BASE_URL_API + "getClientDetails",
            {
                method: 'POST',
                body: JSON.stringify({
                    storeIdentifier: activeStore?.storeIdentifier,
                    phoneNumber: phoneNumber.otp,
                    token: authCtx.token
                })
            });

        if (!getClientDetailsResponse.ok) {
            console.log("Something went wrong : Server Error");
        } else {
            const getClientDetailsRespo = await getClientDetailsResponse.json();
            if (getClientDetailsRespo.status === "success") {
                if (getClientDetailsRespo.message === "user already registered!") {
                    setUserDetailsFetched(true)
                    setUserDetails(getClientDetailsRespo.response)
                } else {
                    setIsUserFormOpen(true)
                }
            } else {
                if (getClientDetailsRespo.message === 'tokenExpired') {
                    authCtx.logout();
                } else {
                    console.log(getClientDetailsRespo);
                }
                setUserDetails(null);
            }
            setIsLoading(false)
        }

    }

    const phoneNumberHandler = (otp) => {
        setPhoneNumber({ otp });
        if (otp.length < 10) {
            setUserDetailsFetched(false)
        }
    }

    const userDetailsFetchHandler = () => {
        getClientDetails()
        // setUserDetailsFetched(!userDetailsFetched)
        // setIsUserFormOpen(true)
    }


    const closeModal = () => {
        setIsUserFormOpen(false);
    }

    const [membershipCards, setMembershipCards] = useState([
        {
            membershipIdentifier: 1,
            name: 'Basic Membership',
            price: 29.99,
            duration: '1 Month',
            colorClass: 'basicCard',
            features: ['Basic access', 'Email support', 'Mobile app'],
            icon: <User className={styles.cardIcon} />,
            popular: false
        },
        {
            id: 2,
            name: 'Premium Membership',
            price: 79.99,
            duration: '3 Months',
            colorClass: 'premiumCard',
            features: ['Premium access', 'Priority support', 'Advanced features', 'Monthly rewards'],
            icon: <Star className={styles.cardIcon} />,
            popular: true
        },
        {
            id: 3,
            name: 'VIP Membership',
            price: 149.99,
            duration: '6 Months',
            colorClass: 'vipCard',
            features: ['VIP access', '24/7 support', 'All premium features', 'Exclusive events', 'Personal manager'],
            icon: <Shield className={styles.cardIcon} />,
            popular: false
        },
        {
            id: 4,
            name: 'Elite Membership',
            price: 249.99,
            duration: '1 Year',
            colorClass: 'eliteCard',
            features: ['Elite access', 'Dedicated support', 'All features included', 'Priority booking', 'Cashback rewards'],
            icon: <Zap className={styles.cardIcon} />,
            popular: false
        }
    ]);

    const steps = [
        { number: 1, title: 'Select Card', icon: <CreditCard className={styles.stepIcon} /> },
        { number: 2, title: 'Customer Info', icon: <User className={styles.stepIcon} /> },
        { number: 3, title: 'Payment', icon: <IndianRupee className={styles.stepIcon} /> },
        { number: 4, title: 'Confirmation', icon: <Check className={styles.stepIcon} /> }
    ];

    const nextStep = () => {
        if (currentStep < 4) setCurrentStep(currentStep + 1);
    };

    const prevStep = () => {
        if (currentStep > 1) setCurrentStep(currentStep - 1);
    };

    const [gstAdded, setGstAdded] = useState(true)
    const [gt, setGt] = useState(0)
    const [payMode, setPayMode] = useState(null)
    const [responseMsg, setResponseMsg] = useState('');


    const handleCardSelect = (card) => {
        setSelectedCard(card);
        nextStep();
    };

    const formatCardNumber = (value) => {
        const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
        const matches = v.match(/\d{4,16}/g);
        const match = matches && matches[0] || '';
        const parts = [];
        for (let i = 0, len = match.length; i < len; i += 4) {
            parts.push(match.substring(i, i + 4));
        }
        if (parts.length) {
            return parts.join(' ');
        } else {
            return v;
        }
    };


    const getMemberships = async () => {
        setIsFetching(true)
        const response = await fetch(BASE_URL_API + "getMemberships",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: vendorIdentifier
                })
            });

        if (!response.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const respo = await response.json()
            if (respo?.status === "success") {
                setMembershipCards(respo?.response)
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                } else {
                    setMembershipCards([])
                }
            }
            console.log(respo)
        }
        setIsFetching(false)
    }

    const getAvailableMembershipSeries = async () => {
        // console.log("selectedCard : ", selectedCard?.membershipIdentifier)
        setIsFetchingSeries(true)
        const response = await fetch(BASE_URL_API + "getAvailableMembershipSeries",
            {
                method: "POST",
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: vendorIdentifier,
                    membershipIdentifier: selectedCard?.membershipIdentifier
                })
            });

        if (!response.ok) {
            console.log("Something went wrong : Server Error")
        } else {
            const respo = await response.json()
            if (respo?.status === "success") {
                setAvailableSeries(respo?.response)
            } else {
                if (respo?.message === "tokenExpired") {
                    authCtx.logout()
                } else {
                    setAvailableSeries([])
                }
            }
            console.log(respo)
        }
        setIsFetchingSeries(false)
    }



    const purchaseCard = async () => {
        console.log("All records : ", selectedCard, selectCardNumber, userDetails, gstAdded, payMode)
        setIsCreating(true)
        const purchaseCardResponse = await fetch(BASE_URL_API + "sellAMembership",
            {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    cardNumber: selectCardNumber?.value,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: vendorIdentifier,
                    storeName: activeStore?.storeName,
                    storePrimaryPhone: activeStore?.storePrimaryPhone,
                    membershipIdentifier: selectedCard?.membershipIdentifier,
                    membershipName: selectedCard?.name,
                    amountPaid: selectedCard?.membershipPrice,
                    clientName: userDetails.client_name,
                    clientStoreRelationIdentifier: userDetails.clientStoreRelationIdentifier,
                    mobile: userDetails.mobile,
                    hasGstPaid: gstAdded,
                    cardWorth: selectedCard.membershipWorthOff,
                    payMode: payMode,
                    duration: selectedCard?.validityDuration
                })
            });

        if (!purchaseCardResponse.ok) {
            console.log("Something went wrong : Server Error");
        } else {
            const purchaseCardRespo = await purchaseCardResponse.json();
            if (purchaseCardRespo.status === "success") {
                navigate(`/memberships/home`);
                toast(`Successfully activated the membership`)
            } else {
                if (purchaseCardRespo.message === "tokenExpired") {
                    authCtx.logout();
                } else {
                    setResponseMsg(purchaseCardRespo.message);
                }
            }
            console.log(purchaseCardRespo);
        }
        setIsCreating(false)


    }

    const renderStepIndicator = () => (
        <div className={styles.stepIndicator}>
            <div className={styles.stepsContainer}>
                {steps.map((step, index) => (
                    <div key={step.number} className={styles.stepWrapper}>
                        <div className={`${styles.stepCircle} ${currentStep >= step.number ? styles.stepActive : styles.stepInactive}`}>
                            {currentStep > step.number ? <Check className={styles.stepIcon} /> : step.icon}
                        </div>
                        <span className={`${styles.stepTitle} ${currentStep >= step.number ? styles.stepTitleActive : styles.stepTitleInactive}`}>
                            {step.title}
                        </span>
                        {index < steps.length - 1 && (
                            <div className={`${styles.stepConnector} ${currentStep > step.number ? styles.stepConnectorActive : styles.stepConnectorInactive}`} />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );

    const renderCardSelection = () => (
        <div className={styles.cardSelection}>
            <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Choose Membership Card</h2>
                <p className={styles.sectionSubtitle}>Select the perfect membership for your customer</p>
            </div>

            <div className={styles.cardsGrid}>
                {membershipCards.map((card) => (
                    <div
                        key={card?.membershipIdentifier}
                        onClick={() => handleCardSelect(card)}
                        className={`${styles.membershipCard} ${card?.popular ? styles.popularCard : ''}`}
                    >
                        {card?.popular && (
                            <div className={styles.popularBadge}>
                                <span>MOST POPULAR</span>
                            </div>
                        )}

                        <div className={`${styles.cardVisual} ${styles[card?.colorClass]}`}>
                            <div className={styles.cardIconContainer}>
                                {membershipIcons[card?.icon]}
                            </div>
                            <div className={styles.cardPricing}>
                                <div className={styles.cardPrice}>₹{card?.membershipPrice}</div>
                                <div className={styles.cardDuration}>{card?.validityDuration} Days</div>
                            </div>
                        </div>

                        <h3 className={styles.cardName}>{card?.name}</h3>
                        <ul className={styles.featuresList}>
                            {card?.features?.map((feature, index) => (
                                <li key={index} className={styles.featureItem}>
                                    <Check className={styles.featureIcon} />
                                    {feature}
                                </li>
                            ))}
                        </ul>

                        <button className={styles.selectButton}>
                            Select This Card
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderCustomerForm = () => (
        <div className={styles.formContainer}>
            <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Customer Information</h2>
                <p className={styles.sectionSubtitle}>Enter customer details for membership registration</p>
            </div>
            {availableSeries?.length > 0 ?
                <div className={styles.sectionContent}>
                    {selectedCard && (
                        <div className={styles.selectedCardInfo}>
                            <div className={styles.selectedCardContent}>
                                <div>
                                    <h3 className={styles.selectedCardName}>{selectedCard.name}</h3>
                                    <p className={styles.selectedCardDetails}>{selectedCard.validityDuration} Days - ₹{selectedCard.membershipPrice}</p>
                                </div>
                                <button
                                    onClick={() => setCurrentStep(1)}
                                    className={styles.changeCardButton}
                                >
                                    Change Card
                                </button>
                            </div>

                            <div>
                                <div className={styles.amsTitle} >Available Membership Series!</div>
                                <div>
                                    <Select
                                        className={styles.reactSelect}
                                        options={availableSeries}
                                        placeholder="Select A Card Number"
                                        value={selectCardNumber}
                                        onChange={(selected) => setSelectCardNumber(selected)}
                                        isSearchable
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={styles.clientDetailsWrapper}>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Enter Client's Phone Number</label>
                            <div className={styles.phoneWrapper}>
                                <OtpInput
                                    value={phoneNumber.otp}
                                    numInputs={10}
                                    shouldAutoFocus={true}
                                    onChange={(otp) => phoneNumberHandler(otp)}
                                    isInputNum={true}
                                    containerStyle={styles.numberInputWrapper}
                                    inputStyle={styles.numberInput}
                                    separator={<>-</>}
                                />
                                {phoneNumber.otp.length === 10 &&
                                    <div className={styles.getDetailsBtnWrapper}>
                                        {isLoading === false ?
                                            <button onClick={() => userDetailsFetchHandler()} className={`${styles.getDetailsBtn}`} >Get Details</button>
                                            :
                                            <button onClick={() => userDetailsFetchHandler()} className={`${styles.getDetailsBtn}`} ><BiLoaderAlt size={16} className={styles.spinner} /> Getting...</button>
                                        }
                                    </div>
                                }
                            </div>
                        </div>
                        <div className={styles.userDetailsWrapper}>
                            {userDetailsFetched === true &&
                                <div className={styles.eachInputSection}>
                                    <div className={styles.clientName}>
                                        {userDetails.client_name}
                                    </div>
                                    <div className={styles.duration}>
                                        Registered {humanReadableTimePassed(userDetails?.added_on)}
                                    </div>
                                </div>
                            }
                        </div>

                    </div>
                </div>
                :
                <div className={styles.sectionContent}>
                    <div className={styles.noSeriesFound}>
                        <div> There's no available card number series available for this membership.
                        </div>
                        <div>
                            Kindly generate membership card numbers to issues / sell a membership card.
                        </div>
                        <div>
                            <Link to={`/memberships/settings/${selectedCard?.membershipIdentifier}/series`}
                                className={styles.primaryButton}
                            >
                                Generate Now
                            </Link>
                        </div>
                    </div>
                </div>
            }


        </div>
    );

    const renderPaymentForm = () => (
        <div className={styles.formContainer}>
            <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Payment Information</h2>
                <p className={styles.sectionSubtitle}>Secure payment processing for membership activation</p>
            </div>

            <div className={styles.sectionContent}>
                <div className={styles.payModeWrapper}>

                    <div className={styles.orderSummary}>
                        <div className={styles.summaryHeader}>
                            <div className={styles.summaryTitle}>Order Summary</div>
                        </div>
                        <div className={styles.summaryContent}>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>{selectedCard?.name}</span>
                                <span className={styles.summaryValue}>₹{parseFloat(selectedCard?.membershipPrice).toFixed(2)}</span>
                            </div>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>GST (18%)
                                    <div className={styles.gstToggleWrapper}>
                                        {gstAdded === true ?
                                            <button className={styles.gstRemove}
                                                onClick={() => setGstAdded(!gstAdded)}
                                            >Remove</button>
                                            :
                                            <button className={styles.gstAdd}
                                                onClick={() => setGstAdded(!gstAdded)}
                                            >Add</button>
                                        }
                                    </div>
                                </span>
                                {gstAdded === true ?
                                    <span className={styles.summaryValue}>₹{parseFloat(parseFloat(selectedCard?.membershipPrice) * 0.18).toFixed(2)}</span>
                                    :
                                    <span className={styles.summaryValue}>₹0.00</span>
                                }
                            </div>
                            <div className={styles.summaryRow}>
                                <span className={styles.summaryLabel}>Duration</span>
                                <span className={styles.summaryValue}>{selectedCard?.duration}</span>
                            </div>
                            <div className={`${styles.summaryRow} ${styles.summaryTotal}`}>
                                <span className={styles.totalLabel}>Total</span>
                                <span className={styles.totalValue}>₹{gstAdded === true ? parseFloat(parseFloat(selectedCard?.membershipPrice) * 1.18).toFixed(2) : parseFloat(selectedCard?.membershipPrice).toFixed(2)}</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.securityNotice}>
                        <Shield className={styles.securityIcon} />
                        <span className={styles.securityText}>
                            Your payment is secured with 256-bit SSL encryption
                        </span>
                    </div>
                </div>

                <div className={styles.paymentSummaryWrapper}>
                    <div className={styles.paymSum}>
                        <div className={styles.pmTitle}>
                            Payment mode
                        </div>
                        <div className={styles.pmBtns}>
                            <button className={payMode === 'Cash' ? styles.payModeBtnSelected : styles.payModeBtn}
                                onClick={() => setPayMode('Cash')}
                            >
                                Cash
                            </button>
                            <button className={payMode === 'Card' ? styles.payModeBtnSelected : styles.payModeBtn}
                                onClick={() => setPayMode('Card')}
                            >
                                Card
                            </button>
                            <button className={payMode === 'Qr' ? styles.payModeBtnSelected : styles.payModeBtn}
                                onClick={() => setPayMode('Qr')}
                            >
                                Qr
                            </button>
                            <button className={payMode === 'Link' ? styles.payModeBtnSelected : styles.payModeBtn}
                                onClick={() => setPayMode('Link')}
                            >
                                Link
                            </button>
                        </div>
                    </div>
                </div>

            </div>


        </div>
    );

    const renderConfirmation = () => (
        <div className={styles.confirmationContainer}>
            <div className={styles.successIcon}>
                <Check className={styles.successCheckIcon} />
            </div>

            <h2 className={styles.confirmationTitle}>Membership Activate Now!</h2>
            <p className={styles.confirmationSubtitle}>
                To successfully activate membership click on Activate Now.
            </p>

            <div className={styles.submitBtnSection}>
                {isCreating === true ?
                    <ShimmerButton className={styles.shimmerBtn} size="md" />
                    :
                    <button onClick={() => purchaseCard()} >Activate Now</button>
                }
            </div>

            <div className={styles.membershipDetails}>
                <h3 className={styles.detailsTitle}>Membership Details</h3>
                <div className={styles.detailsContent}>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Customer:</span>
                        <span className={styles.detailValue}>{userDetails.client_name}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Email:</span>
                        <span className={styles.detailValue}>{userDetails.mobile}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Membership:</span>
                        <span className={styles.detailValue}>{selectedCard?.name}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Duration:</span>
                        <span className={styles.detailValue}>{selectedCard?.validityDuration}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Amount Paid:</span>
                        <span className={`${styles.detailValue} ${styles.paidAmount}`}>₹{selectedCard?.membershipPrice}</span>
                    </div>
                    <div className={styles.detailRow}>
                        <span className={styles.detailLabel}>Activation Date:</span>
                        <span className={styles.detailValue}>{new Date().toLocaleDateString()}</span>
                    </div>
                </div>
            </div>


        </div>
    );

    useEffect(() => {
        if (activeStore !== null) {
            getMemberships()
        }
    }, [asCtx])

    useEffect(() => {
        if (selectedCard !== null) {
            getAvailableMembershipSeries()
        }
    }, [selectedCard])

    useEffect(() => {
        if (currentStep === 1) {
            setIsNextBtnDisable(!selectedCard)
        }

        if (currentStep === 2) {
            setIsNextBtnDisable(!(selectedCard && selectCardNumber && userDetails))
        }

        if (currentStep === 3) {
            setIsNextBtnDisable(!(selectedCard && selectCardNumber && payMode && userDetails))
        }
    }, [selectCardNumber, userDetails, selectedCard, currentStep, payMode])


    if (activeStore === null) {
        return (
            <div>
                Please select a store first!
            </div>
        )
    }


    return (
        <div className={styles.mainWrapper}>
            <MembershipNavbar />
            <AddNewClient isOpen={isUserFormOpen} closeModal={closeModal} phone={phoneNumber.otp} />
            <div className={styles.container}>
                <div className={styles.mainCard}>
                    <div className={styles.upperWrapper} >
                        <div className={styles.header}>
                            <div className={styles.mainTitle}>Sell Membership</div>
                            <div className={styles.mainSubtitle}>Issue new memberships to customers with ease</div>
                        </div>
                        <div className={styles.navigation}>
                            <button
                                onClick={prevStep}
                                className={`${styles.secondaryButton} ${styles.prevBtn}`}
                                disabled={currentStep < 1}
                            >
                                <ArrowLeft className={styles.buttonIcon} />
                                Previous
                            </button>
                            {renderStepIndicator()}
                            <button
                                onClick={nextStep}
                                disabled={isNextBtnDisable}
                                className={`${styles.primaryButton} ${styles.nextBtn} ${isNextBtnDisable ? styles.disabledButton : ''}`}
                            >
                                {currentStep === 3 ? 'Payment' : 'Next'}
                                <ArrowRight className={styles.buttonIcon} />
                            </button>


                        </div>


                    </div>
                    <div className={styles.content}>
                        {currentStep === 1 && renderCardSelection()}
                        {currentStep === 2 && renderCustomerForm()}
                        {currentStep === 3 && renderPaymentForm()}
                        {currentStep === 4 && renderConfirmation()}
                    </div>


                </div>
            </div>
        </div>
    );
};

export default SellMembership;